import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useAuthContext } from '../../../../context/auth.context'
import { useQueryParam } from '../../../../shared/hooks/location'
import { useLoad } from '../../../../shared/hooks/load'
import { LoginDeviceModal } from './login-device-modal'
import { useModalActions } from '../../../../shared/component/modal-dialog'
import { IconLoginDevice } from '../../../../shared/icon/icon'
import { LoginErrorAlert } from './login-error-alert'
import { useApiFormAuthEmail, useApiListAuthProviderLink, useApiListProviderDomainStatus } from '../../../../api-new/state/auth'
import { useBuildRouteArgs, useLoginRedirect } from '../hooks/redirect'
import { authPath, carryParams, domainOf, providerLinkKey } from '../hooks/auth-path'
import { fetchDomainStatus, passwordAllowedBy } from '../hooks/domain-status'
import { reasonNotice } from './login-reason-notice'
import { consumeLogoutMessage, consumeLogoutReason } from '../../../../context/logout-destination'
import { AuthDivider, AuthHead, AuthNotice, AuthPage } from '../../../../shared/sso-workspace/auth-card'
import { useIsMobile } from '../../../../shared/hooks/use-is-mobile'
import { registerState } from './register'
import { AccountChooser } from './account-chooser'
import { forgetAccount, forgetAllAccounts, readRecentAccounts } from '../../../../shared/recent-accounts/recent-accounts'
import { isLaunchSignIn, isSharedDevice, setSharedDevice, signInAndRemember } from '../../../../shared/recent-accounts/sign-in-memory'
import { takeSignInHandoff } from '../../../../shared/recent-accounts/sign-in-handoff'
import { useProviderSignin } from '../hooks/provider'
import { SignInMethods, usePublicSignInMethods } from './sign-in-methods'

const DEVICE_MODAL = 'device'

// /auth/email: the first sign-in step. The person types their email; if a
// connection claims it they go to /auth/providers (and on to their organization's
// sign-in), otherwise they get the password field here.
//
// Query it reads:
//   email=        prefill (Back and "Change" from the connection chooser,
//                 the error page's "Sign in with your password")
//   use=password  skip the connection lookup and go straight to the
//                 password step (the chooser's "Use my password instead")
//   use=another   the email field, not "Choose an account" (the account
//                 menu's "Add another account", as the chooser's "Use
//                 another account")
//   handoff=      the account menu's sign-in for email= (below)
//   reason=       why they were signed out (login-reason-notice.js)
//   redirect=, redirect-doc=  where to go after signing in; carried forward,
//                 into /auth/register too
//
// Require SSO (owner 2026-10-06): before the password step opens, the
// domain status says whether the server takes a password at this domain at
// all. When it answers passwordAllowed: false -- most often a bookmarked or
// old ?use=password link -- the page says "<domain> signs in with SSO." and
// its button goes to the organization's sign-in instead. A failed lookup
// opens the password step as before.
//
// Recent accounts (owner 2026-10-25, shared/recent-accounts): with no email
// in the URL and accounts remembered in this browser, the page opens on
// "Choose an account" (account-chooser.js). A row continues exactly as if
// its email had been typed (handleNext: the server decides SSO, password,
// the chooser or sign-up); "Use another account" opens the email field.
// Every sign-in here is remembered (recordSignIn; owner 2026-10-30: no
// remember box, as Google does it) except a private one: "Not your device?
// Sign in privately" at the bottom of the first page sets the session cookie
// edxr-shared-device (sign-in-memory.js) and a notice at the top says so, with
// Turn off; a sign-in made for an LMS launch (redirect=/lms/launch/...) is
// private on its own, with no list, notice or toggle. "← Back" (only with
// remembered accounts) returns to the list.
//
// Sign in from the account menu (owner 2026-11-02; sign-in-handoff.js): the
// menu opens this page in a new tab, signed out, with email=<the account>
// and handoff=<nonce>. The handoff is taken once, only for that email; with
// it the page goes on at once, exactly as choosing the account's row does.
// Without it (a reload, a crafted or old link) the email is only filled in.
//
// Sign-in methods (sign-in-methods.js): under "or", a button per method
// offered to everyone -- "Sign in with Microsoft" where auth offers it (the
// Microsoft Entra app gallery's entry point). It starts a sign-in with no
// email; the email step above works exactly as before.
export const LoginEmail = () => {
  const history = useHistory()

  // Auto-focusing the email field pops the on-screen keyboard on phones the
  // instant this page loads. On a narrow viewport that's very likely a
  // touch/mobile visit, and stealing focus for a field the person may not
  // want (they might be here for "Sign in from another device" instead)
  // costs more than it saves: a keyboard-focused input can eat the first tap
  // elsewhere on the page as a blur/dismiss rather than a click on the
  // intended target, which reads as "the button didn't work" until a second
  // tap. Desktop keeps the autofocus (no keyboard to fight with).
  const isMobile = useIsMobile()

  const apiFormAuthEmail = useApiFormAuthEmail()
  const apiListAuthProviderLink = useApiListAuthProviderLink()
  const apiListProviderDomainStatus = useApiListProviderDomainStatus()

  const authContext = useAuthContext()

  const loginRedirect = useLoginRedirect()

  const initialEmail = useQueryParam('email') || ''
  const usePassword = useQueryParam('use') === 'password'
  const useAnother = useQueryParam('use') === 'another'
  // The account menu's sign-in for this email: taken once, on mount.
  const handoff = useQueryParam('handoff')
  const [handedOff] = useState(() => !!initialEmail && takeSignInHandoff(handoff, initialEmail))

  const [email, updateEmail] = useState(initialEmail)
  const [password, updatePassword] = useState('')
  const [showPassword, updateShowPassword] = useState(false)
  const [revealPassword, updateRevealPassword] = useState(false)
  const [ssoSuspended, updateSsoSuspended] = useState(false)
  // { email, domain } when that email's domain signs in with SSO only.
  const [ssoOnly, updateSsoOnly] = useState(null)
  const [checking, updateChecking] = useState(false)
  // A username with no account: sign-up needs an email address (owner 2026-10-22).
  const [unknownName, updateUnknownName] = useState('')
  const [submitting, updateSubmitting] = useState(false)

  // The email the password step was opened for. Editing the email after
  // that and pressing Enter looks it up again instead of signing in.
  const checkedEmail = useRef(null)

  // Recent accounts: read once; the chooser shows when there are some and
  // the URL names no email. editing: the chooser's remove mode. chosenEmail:
  // the row being looked up.
  const [accounts, updateAccounts] = useState(() => readRecentAccounts())
  const lmsLaunch = isLaunchSignIn(useQueryParam('redirect'))
  const [mode, updateMode] = useState(() => (!initialEmail && !useAnother && accounts.length && !lmsLaunch ? 'choose' : 'email'))
  const [editing, updateEditing] = useState(false)
  const [chosenEmail, updateChosenEmail] = useState(null)
  const [privateSignIn, updatePrivateSignIn] = useState(() => isSharedDevice())
  const [focusEmail, updateFocusEmail] = useState(false)
  const [announcement, updateAnnouncement] = useState('')
  const emailRef = useRef(null)

  let redirect = useQueryParam('redirect')
  let redirectDoc = useQueryParam('redirect-doc')
  // The query string carries the reason when our own navigation won; the
  // stash carries it when the unauthenticated guard's redirect won instead
  // (see context/logout-destination.js). Consumed once per mount.
  const stashedReason = useMemo(() => consumeLogoutReason(), [])
  // The server's own sentence for the session it ended (Require SSO's names
  // the connection), stashed the same way; never read from the address.
  const stashedMessage = useMemo(() => consumeLogoutMessage(), [])
  let reason = useQueryParam('reason') || stashedReason
  const notice = reasonNotice(reason, stashedMessage)

  const buildRouteArgs = useBuildRouteArgs()

  const deviceModal = useModalActions(DEVICE_MODAL)

  // The sign-in methods offered to everyone, and the one starting.
  const methods = usePublicSignInMethods()
  const providerSignin = useProviderSignin()
  const [startingMethod, updateStartingMethod] = useState(null)
  const [failedMethod, updateFailedMethod] = useState(null)

  const handleMethod = async method => {
    updateFailedMethod(null)
    updateStartingMethod(method)
    try {
      await providerSignin(method, '', redirect, redirectDoc)
    } catch {
      updateStartingMethod(null)
      updateFailedMethod(method)
    }
  }

  // Back from the identity provider's page restores this one from the
  // browser's page cache as it was left: "Taking you to ..." with every
  // button waiting. It starts again ready.
  useEffect(() => {
    const restored = e => {
      if (e.persisted) updateStartingMethod(null)
    }
    window.addEventListener('pageshow', restored)
    return () => window.removeEventListener('pageshow', restored)
  }, [])

  // Answers 'navigated' when the page went on (a connection, sign-up),
  // 'password' when the password step opened, else 'stayed'. value: the email to look up (a chosen account's), else
  // the field's.
  const handleNext = async ({ skipConnections, value: given } = {}) => {
    const value = (given === undefined ? email : given).trim()

    if (!value) {
      return 'stayed'
    }

    updateSsoSuspended(false)
    updateSsoOnly(null)
    updateChecking(true)
    updateUnknownName('')

    try {
      const domain = domainOf(value)

      if (domain && !skipConnections) {
        // The FULL email, not just its domain: a connection still in testing
        // is limited to named testers, and only the email can match that
        // list (audit B2). A failed lookup falls through to the password
        // step rather than leaving the person stuck on this one.
        let providers = []

        try {
          providers = (await apiListAuthProviderLink.get(providerLinkKey(value))) || []
        } catch {}

        if (providers.length) {
          let { path } = buildRouteArgs('/auth/providers', redirect, redirectDoc, value)
          history.replace(path)
          return 'navigated'
        }
      }

      // No connection was found for this email (or it wasn't asked: the
      // password step was asked for by name). The domain status says whether
      // SSO was suspended (deactivated domain, expired credential), so the
      // password step can point people to a password reset instead of a dead
      // end -- and whether the server will refuse a password here at all.
      // Best effort: a failed lookup (network hiccup, rate limit) answers
      // nothing and never blocks the password step below.
      if (domain) {
        const status = await fetchDomainStatus(apiListProviderDomainStatus, domain)

        if (!passwordAllowedBy(status)) {
          updateShowPassword(false)
          updateSsoOnly({ email: value, domain })
          return 'stayed'
        }

        updateSsoSuspended(!!status && status.sso === 'suspended')
      }

      var emailExists = await apiFormAuthEmail.get(`${value}/exists`)

      // No account: offer to create one, carrying the address in router
      // state (never the URL) so the registration form starts filled in,
      // and where to go after signing in in the query (a course link's
      // sign-in keeps the course through sign-up, owner 2026-10-13).
      // Sign-up opens only with an address this check found no account for
      // (owner 2026-10-22); an unknown username stays here and asks for one.
      if (!emailExists) {
        const state = registerState(value)
        if (!state) {
          updateUnknownName(value)
          return 'stayed'
        }
        history.replace(authPath('/auth/register', carryParams({ redirect, redirectDoc })), state)
        return 'navigated'
      }

      checkedEmail.current = value
      updateShowPassword(true)
      return 'password'
    } catch {
    } finally {
      updateChecking(false)
    }

    return 'stayed'
  }

  // Back to the first step for another email (or none), with no notice or
  // error left from the last one.
  const resetSteps = value => {
    updateEmail(value)
    updatePassword('')
    updateRevealPassword(false)
    updateShowPassword(false)
    updateSsoOnly(null)
    updateSsoSuspended(false)
    updateUnknownName('')
    checkedEmail.current = null
    if (apiFormAuthEmail.init) apiFormAuthEmail.init()
  }

  // A remembered account: the same as typing its email and pressing
  // Continue. The page stays on the chooser while it asks, and moves to the
  // email step only when the answer is a step here: the password (its field
  // takes focus), or an SSO-only domain, an unknown name or an error (the
  // email field takes it, but not on a phone, where it would pop the
  // keyboard over the notice).
  const handleChoose = async account => {
    if (checking) return
    resetSteps(account.email)
    updateChosenEmail(account.email)
    const outcome = await handleNext({ value: account.email })
    if (outcome !== 'navigated') {
      updateMode('email')
      if (outcome !== 'password' && !isMobile) updateFocusEmail(true)
    }
    updateChosenEmail(null)
  }

  // The account menu's sign-in: the same as choosing the account's row, on
  // the email step (the address names the account). The password field
  // takes focus itself; any other answer that stays here (an SSO-only
  // domain, an error) gives it to the email field, but not on a phone.
  const handleHandoff = async () => {
    const outcome = await handleNext({ value: initialEmail })
    if (outcome !== 'navigated' && outcome !== 'password' && !isMobile) updateFocusEmail(true)
  }

  const handleUseAnother = () => {
    resetSteps('')
    updateMode('email')
    updateFocusEmail(true)
  }

  const handlePrivate = on => {
    setSharedDevice(on)
    updatePrivateSignIn(on)
  }

  const handleBackToAccounts = () => {
    const remembered = readRecentAccounts()
    updateAccounts(remembered)
    if (!remembered.length) return
    resetSteps('')
    updateEditing(false)
    updateMode('choose')
  }

  // Removing the last account (or all of them) falls back to the email
  // field, and says so (the chooser's own announcement goes with it).
  const leaveChooser = message => {
    updateEditing(false)
    resetSteps('')
    updateMode('email')
    updateFocusEmail(true)
    updateAnnouncement(message)
  }

  const handleRemove = account => {
    const remaining = forgetAccount(account.email)
    updateAccounts(remaining)
    if (!remaining.length) leaveChooser(`${account.email} won't be suggested on this device.`)
  }

  const handleForgetAll = () => {
    updateAccounts(forgetAllAccounts())
    leaveChooser('No accounts are remembered on this device now.')
  }

  useEffect(
    () => {
      if (focusEmail && mode === 'email' && emailRef.current) {
        emailRef.current.focus()
        updateFocusEmail(false)
      }
    },
    [focusEmail, mode]
  )

  // "Use my password instead" and the error page's password action land here
  // with the email already known: go straight to the password step.
  useLoad(() => {
    if (usePassword && initialEmail) {
      handleNext({ skipConnections: true })
    } else if (handedOff) {
      handleHandoff()
    }
  }, [])

  // The SSO notice belongs to the email it was found for; editing the email
  // puts the page back to Continue.
  const ssoOnlyNow = ssoOnly && email.trim() === ssoOnly.email ? ssoOnly : null

  const handleSubmit = async e => {
    e.preventDefault()

    if (ssoOnlyNow) {
      let { path } = buildRouteArgs('/auth/providers', redirect, redirectDoc, ssoOnlyNow.email)
      history.replace(path)
      return
    }

    if (!showPassword || !password || email.trim() !== checkedEmail.current) {
      await handleNext({ skipConnections: usePassword && email.trim() === initialEmail.trim() })
      return
    }

    updateSubmitting(true)

    try {
      let authToken = await apiFormAuthEmail.post({ email: email.trim(), password })
      signInAndRemember(authContext.setAuthToken, authToken, { method: { kind: 'password' }, sharedDevice: privateSignIn, launch: lmsLaunch })
      loginRedirect()
    } catch {
    } finally {
      updateSubmitting(false)
    }
  }

  const forgotPath = authPath('/auth/password-recover/request', { email: email.trim() })

  const busy = checking || submitting

  const choosing = mode === 'choose' && accounts.length > 0

  const head = choosing
    ? {
        title: editing ? 'Remove an account' : 'Choose an account',
        lede: editing ? "Removing an account only forgets it on this device. It doesn't sign anyone out or delete anything." : 'Pick an account to continue.'
      }
    : {
        title: showPassword ? 'Sign in' : 'Sign in or create an account',
        lede: showPassword ? 'Enter your password to continue.' : "Enter your email to continue. New here? We'll help you set up your account. If your organization has its own sign-in, we'll take you there."
      }

  return (
    <React.Fragment>
      <AuthPage labelledBy="login-title">
        {!choosing && accounts.length > 0 && !lmsLaunch &&
          <button type="button" className="btn btn-link sa-back" disabled={busy} aria-label="Back to your accounts" onClick={handleBackToAccounts}>
            ← Back
          </button>}

        <AuthHead id="login-title" title={head.title} lede={head.lede} />

        {privateSignIn && !lmsLaunch &&
          <AuthNotice id="login-private">
            <p>
              Private sign-in: this account won't be remembered on this device.{' '}
              <button type="button" className="btn btn-link sa-inline-link" disabled={busy} onClick={() => handlePrivate(false)}>Turn off</button>
            </p>
          </AuthNotice>}

        {notice && <AuthNotice>{notice}</AuthNotice>}

        <LoginErrorAlert error={apiFormAuthEmail.error} />

        {choosing &&
          <AccountChooser
            accounts={accounts}
            editing={editing}
            onEditingChange={updateEditing}
            busyEmail={chosenEmail}
            onChoose={handleChoose}
            onUseAnother={handleUseAnother}
            onRemove={handleRemove}
            onForgetAll={handleForgetAll}
            labelledBy="login-title" />}

        <p className="visually-hidden" role="status">{announcement}</p>

        {/* Autofill (owner 2026-09-29): on the first step the field is
            autocomplete="email", not "username" -- Safari asks for Touch ID
            before filling anything from a saved credential, and someone
            about to be sent to their organization's single sign-on never
            needs a password. Once the password step opens, the same field
            becomes the form's username, so the password manager fills the
            pair (one Touch ID, at the password) and saving a new password
            stores it against this email. Usernames still work: the field
            stays type="text". */}
        {!choosing &&
        <form className="sa-form" noValidate onSubmit={handleSubmit}>
          {unknownName &&
            <AuthNotice id="login-unknown-name">
              <p className="m-0">We couldn't find an account for <strong>{unknownName}</strong>. To create one, enter your email address.</p>
            </AuthNotice>}

          {ssoOnlyNow &&
            <AuthNotice id="login-sso-only">
              <p>{ssoOnlyNow.domain} signs in with SSO.</p>
            </AuthNotice>}

          <div className="sw-field">
            <label className="sw-label" htmlFor="email">Email</label>
            <input
              ref={emailRef}
              id="email"
              name="email"
              className="form-control"
              type="text"
              inputMode="email"
              autoComplete={showPassword ? 'username' : 'email'}
              autoCapitalize="none"
              spellCheck={false}
              placeholder="name@example.org"
              autoFocus={!initialEmail && !isMobile}
              value={email}
              onChange={e => updateEmail(e.target.value)}
            />
          </div>

          {showPassword &&
            <React.Fragment>
              {ssoSuspended &&
                <AuthNotice tone="warn" id="login-sso-suspended">
                  <p>
                    Single sign-on for your organization is paused, so sign in with your password instead. Never set one?{' '}
                    <Link to={forgotPath}>Set a password</Link>.
                  </p>
                </AuthNotice>}

              <div className="sw-field">
                <div className="sa-label-row">
                  <label className="sw-label" htmlFor="password">Password</label>
                  <Link to={forgotPath}>Forgot password?</Link>
                </div>
                <div className="input-group">
                  <input
                    id="password"
                    name="password"
                    className="form-control"
                    type={revealPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    autoFocus
                    value={password}
                    onChange={e => updatePassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn sw-btn"
                    aria-controls="password"
                    aria-label={revealPassword ? 'Hide password' : 'Show password'}
                    onClick={() => updateRevealPassword(!revealPassword)}>
                    {revealPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
            </React.Fragment>}

          <button
            type="submit"
            className="btn sw-btn-primary sa-block"
            disabled={!email.trim() || busy || (showPassword && email.trim() === checkedEmail.current && !password)}>
            {ssoOnlyNow
              ? 'Sign in with SSO'
              : showPassword
                ? submitting ? 'Signing in…' : 'Sign in'
                : checking ? 'Checking…' : 'Continue'}
          </button>
        </form>}

        <AuthDivider />

        <SignInMethods methods={methods} starting={startingMethod} failed={failedMethod} disabled={busy} onChoose={handleMethod} />

        <button type="button" className="btn sw-btn sa-block" onClick={() => deviceModal.show()}>
          <IconLoginDevice />
          Sign in from another device
        </button>

        {!choosing && !lmsLaunch && !privateSignIn &&
          <p className="sa-foot">
            Not your device?{' '}
            <button type="button" className="btn btn-link sa-inline-link" disabled={busy} onClick={() => handlePrivate(true)}>Sign in privately</button>
          </p>}
      </AuthPage>

      <LoginDeviceModal target={DEVICE_MODAL} />
    </React.Fragment>
  )
}
